/**
 * Product Gallery
 * 
 * Handles the image carousel functionality for the image with collapsible section
 */
class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.slides = this.querySelectorAll('.image-with-collapsible__slide');
    this.dots = this.querySelectorAll('.image-with-collapsible__gallery-dot');
    this.prevButton = this.querySelector('.image-with-collapsible__prev');
    this.nextButton = this.querySelector('.image-with-collapsible__next');
    
    this.currentIndex = 0;
    this.slideCount = this.slides.length;
    
    // Touch tracking variables
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.touchThreshold = 50; // Minimum swipe distance
    this.isSwiping = false;
    
    if (this.slideCount <= 1) return;
    
    this.initGallery();
  }
  
  initGallery() {
    // Set up navigation
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.prevSlide.bind(this));
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.nextSlide.bind(this));
    }
    
    // Set up dots
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goToSlide(index);
      });
    });
    
    // Add touch event listeners for mobile swipe
    this.initSwipeSupport();
    
    // Show first slide
    this.updateSlides();
    
    // Add keyboard navigation
    this.addEventListener('keydown', this.handleKeydown.bind(this));
  }
  
  prevSlide() {
    this.currentIndex = (this.currentIndex - 1 + this.slideCount) % this.slideCount;
    this.updateSlides();
  }
  
  nextSlide() {
    this.currentIndex = (this.currentIndex + 1) % this.slideCount;
    this.updateSlides();
  }
  
  goToSlide(index) {
    this.currentIndex = index;
    this.updateSlides();
  }
  
  updateSlides() {
    // Update slides
    this.slides.forEach((slide, index) => {
      slide.classList.toggle('active', index === this.currentIndex);
    });
    
    // Update dots
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentIndex);
      dot.setAttribute('aria-current', index === this.currentIndex ? 'true' : 'false');
    });
  }
    handleKeydown(event) {
    if (event.key === 'ArrowLeft') {
      this.prevSlide();
    } else if (event.key === 'ArrowRight') {
      this.nextSlide();
    }
  }
    initSwipeSupport() {
    // Create swipe indicators if they don't exist
    if (!this.querySelector('.swipe-indicator-left') && !this.querySelector('.swipe-indicator-right')) {
      this.createSwipeIndicators();
    }

    // Touch start event
    this.addEventListener('touchstart', (event) => {
      this.touchStartX = event.changedTouches[0].screenX;
      this.isSwiping = true;
    }, { passive: true });
    
    // Touch move event for dynamic feedback
    this.addEventListener('touchmove', (event) => {
      if (!this.isSwiping) return;
      
      const currentX = event.changedTouches[0].screenX;
      const diff = currentX - this.touchStartX;
      
      // Show indicator based on direction of swipe
      if (Math.abs(diff) > 20) {
        if (diff > 0) {
          this.showSwipeIndicator('left');
        } else {
          this.showSwipeIndicator('right');
        }
      }
    }, { passive: true });
    
    // Touch end event
    this.addEventListener('touchend', (event) => {
      if (!this.isSwiping) return;
      
      this.touchEndX = event.changedTouches[0].screenX;
      this.handleSwipe();
      this.hideSwipeIndicators();
      this.isSwiping = false;
    }, { passive: true });
    
    // Touch cancel event
    this.addEventListener('touchcancel', () => {
      this.hideSwipeIndicators();
      this.isSwiping = false;
    }, { passive: true });
  }
  
  createSwipeIndicators() {
    const leftIndicator = document.createElement('div');
    leftIndicator.className = 'swipe-indicator swipe-indicator-left';
    leftIndicator.innerHTML = '<svg width="20" height="30" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 1L1 7L7 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    
    const rightIndicator = document.createElement('div');
    rightIndicator.className = 'swipe-indicator swipe-indicator-right';
    rightIndicator.innerHTML = '<svg width="20" height="30" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L7 7L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    
    this.appendChild(leftIndicator);
    this.appendChild(rightIndicator);
  }
  
  showSwipeIndicator(direction) {
    const leftIndicator = this.querySelector('.swipe-indicator-left');
    const rightIndicator = this.querySelector('.swipe-indicator-right');
    
    if (direction === 'left') {
      if (leftIndicator) leftIndicator.classList.add('active');
      if (rightIndicator) rightIndicator.classList.remove('active');
    } else {
      if (rightIndicator) rightIndicator.classList.add('active');
      if (leftIndicator) leftIndicator.classList.remove('active');
    }
  }
  
  hideSwipeIndicators() {
    const indicators = this.querySelectorAll('.swipe-indicator');
    indicators.forEach(indicator => indicator.classList.remove('active'));
  }
  
  handleSwipe() {
    const swipeDistance = this.touchEndX - this.touchStartX;
    
    // Check if swipe was significant enough
    if (Math.abs(swipeDistance) > this.touchThreshold) {
      if (swipeDistance > 0) {
        // Swipe right - go to previous slide
        this.prevSlide();
      } else {
        // Swipe left - go to next slide
        this.nextSlide();
      }
    }
  }
}

customElements.define('product-gallery', ProductGallery);
